import React, { useState, useEffect } from 'react';
import { Radar } from 'react-chartjs-2';
import { Chart as ChartJS, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend } from 'chart.js';
import { byString } from '../../../../utils/byString';
import { getLabels } from './statAdapter';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// Define dataType options
const DATA_TYPE_OPTIONS = {
    minmax: 'Min-max',
    pct: 'Percentile',
    rank: 'League Rank',
    z: 'Z-Score',
    raw: 'Raw Averages'
};

// Map stat keys to statAdapter categories and get display labels
const getDisplayLabel = (stat, sport) => {    
    if (stat.category && stat.stat) {
        const labels = getLabels(sport, stat.category);
        let statKey = '';
        switch(sport){
            case 'mlb':
                statKey = stat.stat.split('.')[1];
                return labels[statKey] || stat.stat;
            case 'nfl':
                statKey = stat.stat;
                return labels[statKey] || stat.stat;
            case 'nba':
                statKey = stat.stat;
                return labels[statKey] || stat.stat;
            default:
                return labels[stat.stat] || stat.stat;
        }
    }
    return stat?.stat || '';
};

// Define stat groupings based on creative phase decisions
const STAT_GROUPINGS = {
    nfl: {
        'Team Scoring': [
            {stat: 'off.scoring.1H', category: 'teamScoring'},
            {stat: 'off.scoring.2H', category: 'teamScoring'},
            {stat: 'off.scoring.FG', category: 'teamScoring'},
            {stat: 'calculated.off.redzone_conv_rate', category: 'teamScoring'},
            {stat: 'calculated.off.pass_td_pct', category: 'teamScoring'},
            {stat: 'calculated.off.rush_td_pct', category: 'teamScoring'},
        ],
        'Opponent Scoring': [
            {stat: 'def.pts_allowed.1H', category: 'oppScoring'},
            {stat: 'def.pts_allowed.2H', category: 'oppScoring'},
            {stat: 'def.pts_allowed.FG', category: 'oppScoring'},
            {stat: 'calculated.def.redzone_conv_rate', category: 'oppScoring'},
            {stat: 'calculated.def.pass_td_pct', category: 'oppScoring'},
            {stat: 'calculated.def.rush_td_pct', category: 'oppScoring'},
        ],

        'Team Passing': [
            {stat: 'off.passing.yds', category: 'teamPassing'},
            {stat: 'off.passing.tds', category: 'teamPassing'},
            {stat: 'calculated.off.comp_pct', category: 'teamPassing'},
            {stat: 'off.passing.pass_rating', category: 'teamPassing'},
            {stat: 'off.passing.intended_air_yds', category: 'teamPassing'},
            {stat: 'off.passing.comp_air_yds', category: 'teamPassing'},
        ],
        'Opponent Passing': [
            {stat: 'def.yds_passing', category: 'oppPassing'},
            {stat: 'def.tds_pass', category: 'oppPassing'},
            {stat: 'calculated.def.pass_comp_pct', category: 'oppPassing'},
            {stat: 'def.pass_rating', category: 'oppPassing'},
            {stat: 'def.pass_int_air_yds', category: 'oppPassing'},
            {stat: 'def.pass_air_yds', category: 'oppPassing'},
        ],
        'Team Rushing': [
            {stat: 'off.rushing.att', category: 'teamRushing'},
            {stat: 'off.rushing.yds', category: 'teamRushing'},
            {stat: 'off.rushing.tds', category: 'teamRushing'},
            {stat: 'off.rushing.ybc', category: 'teamRushing'},
            {stat: 'off.rushing.yac', category: 'teamRushing'},
            {stat: 'calculated.off.yds_per_carry', category: 'teamRushing'},
        ],
        'Opponent Rushing': [
            {stat: 'def.rush_att', category: 'oppRushing'},
            {stat: 'def.yds_rushing', category: 'oppRushing'},
            {stat: 'def.tds_rush', category: 'oppRushing'},
            {stat: 'def.rush_ybc', category: 'oppRushing'},
            {stat: 'def.rush_yac', category: 'oppRushing'},
            {stat: 'calculated.def.yds_per_carry', category: 'oppRushing'},
        ],
        'Team Scheme': [
            {stat: 'calculated.off.pass_rate', category: 'teamScheme'},
            {stat: 'calculated.off.rush_rate', category: 'teamScheme'},
            {stat: 'calculated.off.first_downs_per_play', category: 'teamScheme'},
            {stat: 'calculated.off.yds_per_play', category: 'teamScheme'},
            {stat: 'off.possession', category: 'teamScheme'},
            {stat: 'calculated.off.plays_per_min_poss', category: 'teamScheme'},
        ],
        'Opponent Scheme': [
            {stat: 'calculated.def.pass_rate', category: 'oppScheme'},
            {stat: 'calculated.def.rush_rate', category: 'oppScheme'},
            {stat: 'calculated.def.first_downs_per_play', category: 'oppScheme'},
            {stat: 'calculated.def.yds_per_play', category: 'oppScheme'},
        ]
    },
    mlb: {
        'Offensive Efficiency': [
            {stat: 'batting.BA', category: 'batting'},
            {stat: 'batting.OBP', category: 'batting'},
            {stat: 'batting.SLG', category: 'batting'},
            {stat: 'batting.OPS', category: 'batting'}
        ],
        'Hitting Splits': [
            {stat: 'batting.singles', category: 'batting'},
            {stat: 'batting.doubles', category: 'batting'},
            {stat: 'batting.triples', category: 'batting'},
            {stat: 'batting.HR', category: 'batting'},
            {stat: 'batting.BB', category: 'batting'}
        ],
        'Offensive Production': [
            {stat: 'batting.R', category: 'batting'},
            {stat: 'batting.H', category: 'batting'},
            {stat: 'batting.RBI', category: 'batting'},
            {stat: 'batting.LOB', category: 'batting'},
            {stat: 'batting.SO', category: 'batting'}
        ],
        'Pitching Efficiency': [
            {stat: 'pitching.ERA', category: 'pitching'},
            {stat: 'pitching.R', category: 'pitching'},
            {stat: 'pitching.H', category: 'pitching'},
            {stat: 'pitching.HR', category: 'pitching'},
            {stat: 'pitching.SO', category: 'pitching'},
            {stat: 'pitching.BB', category: 'pitching'}
        ]
    },
    nba: {
        'Offensive Production': [
            {stat: 'stats.Points', category: 'teamScoring'},
            {stat: 'stats.FieldGoalsMade', category: 'teamOffense'},
            {stat: 'stats.ThreePointersMade', category: 'teamOffense'},
            {stat: 'calculated.PtsPerPossession', category: 'teamOffense'},
            {stat: 'stats.TrueShootingAttempts', category: 'teamOffense'},
            {stat: 'calculated.FtRate', category: 'teamOffense'}
        ],
        'Rebounding': [
            {stat: 'stats.Rebounds', category: 'twoWayStats'},
            {stat: 'calculated.DefReboundPercentage', category: 'teamDefense'},
            {stat: 'calculated.OffReboundPercentage', category: 'teamOffense'},
            {stat: 'calculated.TotalReboundPercentage', category: 'twoWayStats'}
        ],
        'Playmaking & Control': [
            {stat: 'stats.Assists', category: 'teamOffense'},
            {stat: 'calculated.AssistPercentage', category: 'teamOffense'},
            {stat: 'calculated.AssistToTurnoverRatio', category: 'teamOffense'},
            {stat: 'stats.Turnovers', category: 'teamOffense'},
            {stat: 'calculated.TurnoverRate', category: 'teamOffense'}
        ],
        'Scoring Efficiency': [
            {stat: 'calculated.OffensiveEfficiencyRating', category: 'teamOffense'},
            {stat: 'calculated.EffectiveFgPercentage', category: 'teamOffense'},
            {stat: 'calculated.TrueShootingPercentage', category: 'teamOffense'},
            {stat: 'calculated.ThreePtFgPercentage', category: 'teamOffense'},
            {stat: 'calculated.TwoPtFgPercentage', category: 'teamOffense'},
            {stat: 'calculated.FtPercentage', category: 'teamOffense'}
        ],
        'Shot Selection': [
            {stat: 'calculated.ThreePtAttemptRate', category: 'teamOffense'},
            {stat: 'calculated.PercentagePtsFrom2', category: 'teamScoring'},
            {stat: 'calculated.PercentagePtsFrom3', category: 'teamScoring'},
            {stat: 'calculated.PercentagePtsFromFt', category: 'teamScoring'}
        ],
        'Defense & Disruption': [
            {stat: 'stats.BlockedShots', category: 'teamDefense'},
            {stat: 'stats.Steals', category: 'teamDefense'},
            {stat: 'stats.PersonalFouls', category: 'twoWayStats'},
            {stat: 'calculated.BlockToFoulRatio', category: 'teamDefense'},
        ]
    }
};

// Transform team stats into radar chart format
const transformStatsToRadarData = (homeTeamStats, awayTeamStats, sport, chartType, dataType) => {
    const stats = STAT_GROUPINGS[sport][chartType];
    if (!stats) return null;
    
    const labels = [];
    const homeData = [];
    const awayData = [];
    let max = 0;

    stats.forEach(stat => {
        const statKey = stat?.stat || '';
        // Append the dataType to the statKey
        const statKeyType = statKey + '.' + dataType;
        const homeValue = byString(homeTeamStats, statKeyType);
        const awayValue = byString(awayTeamStats, statKeyType);
        labels.push(getDisplayLabel(stat, sport));
        homeData.push(homeValue);   
        awayData.push(awayValue);
        if(homeValue > max){
            max = homeValue;
        }
        if(awayValue > max){
            max = awayValue;
        }
    });

    return {
        max: max,
        chartData: {
            labels,
            datasets: [
                {
                    label: 'Home Team',
                    data: homeData,
                    borderColor: 'rgba(59, 130, 246, 0.8)', // Blue
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    pointBackgroundColor: 'rgba(59, 130, 246, 1)',
                    pointBorderColor: 'rgba(59, 130, 246, 1)',
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    borderWidth: 2,
                    fill: ['rank', 'z'].includes(dataType) ? false : true
                },
                {
                    label: 'Away Team',
                    data: awayData,
                    borderColor: 'rgba(34, 197, 94, 0.8)', // Green
                    backgroundColor: 'rgba(34, 197, 94, 0.1)',
                    pointBackgroundColor: 'rgba(34, 197, 94, 1)',
                    pointBorderColor: 'rgba(34, 197, 94, 1)',
                    pointRadius: 4,
                    pointHoverRadius: 6,
                    borderWidth: 2,
                    fill: ['rank', 'z'].includes(dataType) ? false : true
                }
            ]
        }
    };
};

// Get the axis min and max depending on the data type
const getAxisInfo = (dataType, sport, rawMax=1) => {
    if(['minmax', 'pct'].includes(dataType)){
        return {min: 0, max: 1, tickStep: 0.25};
    }else if(dataType === 'rank'){
        const leagueInfo = {
            "mlb": {
                teams: 30,
                tickStep: 10
            },
            "nba": {
                teams: 30,
                tickStep: 10
            },
            "nfl": {
                teams: 32,
                tickStep: 8
            }
        }
        return {min: 1, max: leagueInfo[sport].teams, tickStep: leagueInfo[sport].tickStep};
    }else if (dataType === 'z'){
        return {min: -2.5, max: 2.5, tickStep: 1.5};
    }

    // tick step should be whatever is the closed to 5 ticks total (leaving the least remainder)  
    const tickStep = parseFloat((rawMax / 4).toFixed(3));
    return {min: 0, max: rawMax, tickStep: tickStep};
}

// Build radar chart configuration
const buildRadarConfig = (isDarkMode, chartType, dataType, sport, max) => {
    const textColor = isDarkMode ? '#E5E7EB' : '#374151';
    const gridColor = isDarkMode ? '#374151' : '#E5E7EB';
    const axis = getAxisInfo(dataType, sport, max);

    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: textColor,
                    font: {
                        size: 12
                    }
                }
            },
            title: {
                display: true,
                text: chartType + ' (' + DATA_TYPE_OPTIONS[dataType] + ')',
                color: textColor,
                font: {
                    size: 16,
                    weight: 'bold'
                }
            },
           
        },
        scales: {
            r: {
                beginAtZero: true,
                max: axis.max,
                min: axis.min,
                reverse: dataType === 'rank',
                grid: {
                    color: gridColor
                },
                angleLines: {
                    color: gridColor
                },
                pointLabels: {
                    color: textColor,
                    font: {
                        size: 10
                    }
                },
                ticks: {
                    color: textColor,
                    showLabelBackdrop: false,
                    stepSize: axis.tickStep,
                    display: true,
                    callback: function(value) {
                        if(['rank', 'raw'].includes(dataType)){
                            return value;
                        }else if(dataType === 'z'){
                            return value.toFixed(1);
                        }else{
                            return value*100 + '%';
                        }
                    }
                }
            }
        },
        interaction: {
            intersect: false
        }
    };
};

const StatRadarCharts = ({chartType, homeTeamStats, awayTeamStats, sport, homeTeamName, awayTeamName, dataType}) => {
    const [isDarkMode, setIsDarkMode] = useState(false);

    useEffect(() => {
        // Initial theme detection
        const checkTheme = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };
        
        checkTheme();
        
        // Listen for theme changes
        const observer = new MutationObserver(() => {
            checkTheme();
        });
        
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        });
        
        // Cleanup observer on unmount
        return () => observer.disconnect();
    }, []);

    // Early validation - check for required data
    if (!homeTeamStats || !awayTeamStats || typeof homeTeamStats !== 'object' || typeof awayTeamStats !== 'object') {
        return (
            <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-4">
                <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Team statistics are required for radar chart comparison
                </div>
            </div>
        );
    }

    // Detect sport and validate
    if (!sport) {
        return (
            <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-4">
                <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    Unable to detect sport from team statistics
                </div>
            </div>
        );
    }

    // Transform data for radar chart
    const response = transformStatsToRadarData(homeTeamStats, awayTeamStats, sport, chartType, dataType);
    const max = response.max;
    const chartData = response.chartData;
    if (!chartData) {
        return (
            <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-4">
                <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                    No compatible statistics found for {sport.toUpperCase()} radar chart
                </div>
            </div>
        );
    }

    // Build chart configuration
    const chartConfig = buildRadarConfig(isDarkMode, chartType, dataType, sport, max);

    // Update dataset labels with actual team names
    if (chartData.datasets[0]) {
        chartData.datasets[0].label = homeTeamName;
    }
    if (chartData.datasets[1]) {
        chartData.datasets[1].label = awayTeamName;
    }

    return (
        <div className="relative bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-2 sm:p-6">
            <div className="h-100">
                <Radar data={chartData} options={chartConfig} />
            </div>
        </div>
    );
};

export default StatRadarCharts;